import { buildDaysheetDoc } from './daysheet';
import type { DaysheetLayout } from './daysheetLayout';
import { crewRowsFromSelection, personDisplayName, personOfficialName } from './people';
import { generatePdfBlobViaServer } from './pdf';
import { t, pdfLocale } from './pdfI18n';

/**
 * Tour Itinerary PDF builder (2026-09-04).
 *
 * A single document for the WHOLE band tour (not per show): a consolidated
 * multi-day overview + one daysheet per day in chronological order, reusing
 * the existing buildDaysheetDoc (same content as the Day Sheet tab). This is
 * the central tour-manager deliverable the app was missing.
 */

interface ItineraryDay {
  show: any;
  roadmap: any[];
  flights: any[];
  rooming: any[];
  accommodations: any[];
  guests: any[];
  team: any[];
  /** Membres sélectionnés pour CE show (rider du show), renvoyés par l'API. */
  selectedMemberIds?: string[] | null;
  /** « People on this show » déjà résolu par le serveur (nom + poste par band). */
  people?: Array<{ name: string; position?: string }>;
  contacts?: any[];
  distances?: Array<{ from_label: string; to_label: string; mode?: string; distance?: string; duration?: string }>;
}

interface ItineraryInput {
  band?: { id: string; name?: string } | null;
  days: ItineraryDay[];
  /** Optional daysheet layout applied to each embedded daysheet. */
  daysheetLayout?: DaysheetLayout | null;
}

function fmtDate(d: string | undefined): string {
  if (!d) return 'TBD';
  try {
    return new Date(d).toLocaleDateString(pdfLocale(), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return d;
  }
}

const typeIcon = (et?: string): string => {
  const e = (et || '').toLowerCase();
  if (e === 'show' || e === 'concert') return '🎫';
  if (e === 'festival') return '🎪';
  if (e === 'travel') return '🚐';
  if (e === 'off') return '🚫';
  return '📌';
};

/**
 * Un SEUL jour, tel qu'il est imprimé dans le document d'itinéraire (2026-09-20).
 *
 * Source unique : le document complet ET l'aperçu en ligne sous une ligne de la
 * page Itinerary passent par ici, donc ce que l'utilisateur aperçoit est
 * exactement la page qui sera imprimée pour ce jour.
 */
export function buildItineraryDayDoc(
  day: ItineraryDay,
  opts: { band?: { id: string; name?: string } | null; daysheetLayout?: DaysheetLayout | null } = {},
): any {
  return buildDaysheetDoc(
    {
      show: day.show,
      roadmap: day.roadmap,
      guests: day.guests,
      contacts: day.contacts,
      rooming: day.rooming,
      accommodations: day.accommodations,
      flights: day.flights,
      distances: day.distances,
      bandName: opts.band?.name,
      // Nom affiché (nom de scène) ET identité passeport : le rooming et les vols
      // impriment le passeport, BAND AND CREW le nom de scène (2026-11-03).
      members: (day.team || []).map((m: any) => ({
        id: String(m.id),
        name: personDisplayName(m),
        officialName: personOfficialName(m),
      })),
      // Sélection du show résolue par le serveur (nom + poste par band) ; repli sur la
      // sélection brute quand le payload ne la porte pas encore.
      people: (day.people && day.people.length > 0)
        ? day.people
        : crewRowsFromSelection(day.selectedMemberIds || [], day.team as any),
    },
    opts.daysheetLayout ?? undefined,
  );
}

export function buildItineraryDoc(input: ItineraryInput): any {
  const { band, days, daysheetLayout } = input;
  const content: any[] = [];

  // ── Cover / overview page ──
  content.push({
    text: `${band?.name || 'Band'} — ${t('pdf.itinerary.title')}`,
    fontSize: 24,
    bold: true,
    color: '#222',
    alignment: 'center',
    margin: [0, 20, 0, 6],
  });
  content.push({
    text: t('pdf.itinerary.subtitle'),
    fontSize: 13,
    color: '#666',
    alignment: 'center',
    margin: [0, 0, 0, 20],
  });

  // Overview table: date | show | venue/city | type | team count
  content.push({ text: t('pdf.itinerary.overview'), fontSize: 15, bold: true, margin: [0, 8, 0, 6] });
  const overviewRows = days.map((d) => {
    const s = d.show || {};
    const city = [s.venue_name, s.city, s.country].filter(Boolean).join(' · ');
    return [
      { text: fmtDate(s.date) },
      { text: `${typeIcon(s.event_type)} ${s.name || '—'}` },
      { text: city || '—' },
      { text: String((d.team || []).length) },
    ];
  });
  content.push({
    table: {
      widths: ['30%', '*', '26%', '10%'],
      body: [
        [
          { text: t('pdf.itinerary.col.date'), bold: true },
          { text: t('pdf.itinerary.col.show'), bold: true },
          { text: t('pdf.itinerary.col.place'), bold: true },
          { text: t('pdf.itinerary.col.team'), bold: true },
        ],
        ...overviewRows,
      ],
    },
    layout: 'lightHorizontalLines',
    margin: [0, 0, 0, 16],
  });

  // ── One daysheet per day, chronological ──
  days.forEach((day) => {
    content.push({ pageBreak: 'before', text: '' });
    const doc = buildItineraryDayDoc(day, { band, daysheetLayout });
    content.push(...(doc.content || []));
  });

  return {
    content,
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 40],
    defaultStyle: { fontSize: 10 },
  };
}

export async function exportItineraryPdf(input: ItineraryInput): Promise<string> {
  return generatePdfBlobViaServer(buildItineraryDoc(input));
}
